import React, { useState, useEffect, useRef, useMemo } from 'react';
import { auth, database } from './firebase';
import { ref, push, remove, set, update } from 'firebase/database';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import Login from './Login';
import { resolveAccess, onReconnect, clearGrant, hasValidGrant } from './lib/accessGate';
import { AppLogo } from './design/AppLogo';
import './App.css';
import { SpeedInsights } from '@vercel/speed-insights/react';
import FocusShell from './design/FocusShell';
import {
  toDisplayTx,
  buildBudgetRows,
  buildRecurringSeries,
  buildPlannedSeries,
  monthlyRecurringNet,
  adaptProjection,
  accountDisplayBalance,
} from './design/dataAdapters';
import { budgetCountFromEnvelopes } from './design/settings';
import {
  buildProjection,
  assessRunway,
  applyScenarios,
  scaleDeltasOf,
  plannedEvents,
  plansAfterScenarios,
  matchPlans,
  planLinkWrites,
  missedCharges,
  missedEvents,
  missedCandidates,
  missedLinkWrites,
  endedSeriesKeys,
  lifecycleDay,
  seriesAliasMap,
  aliasedSeriesKey,
  varianceFromHistory,
  projectionBands,
  detectRecurringCandidates,
  matchExistingSeries,
  detectTransferPairs,
  projectCardPayments,
  detectInFlightTransfers,
  inFlightCashEvents,
  seriesKeyOf,
  legacySeriesKeyOf,
} from './lib/forecasting';
import { setCategoryMetaOverrides } from './design/categoryMeta';
import { resolveCategories } from './lib/categorize';
import { uploadProfilePhoto, removeProfilePhoto } from './lib/profilePhoto';
import { firebaseSafeKey } from './lib/firebaseKey';
import { spendDelta, looksLikeCardPayment } from './lib/txType';
import {
  CONNECTIONS_NODE,
  BANK_TX_CONNECTION_ID,
  BANK_TX_SOURCES,
} from './lib/dbKeys';
import { Modal } from './design/sheets';
import Toast from './components/Toast';
import EditTransaction from './components/EditTransaction';
import AccountPicker from './components/AccountPicker';
import PlaidLink from './components/PlaidLink';
import { useFirebaseData } from './hooks/useFirebaseData';



/* ── Background sync cadence ─────────────────────────────────────────── */
// Re-sync a long-lived tab every 6 hours; the check runs more often than
// the window so a wake-from-sleep doesn't wait a full cycle.
const AUTO_SYNC_INTERVAL_MS = 6 * 60 * 60 * 1000;
const AUTO_SYNC_CHECK_MS = 15 * 60 * 1000;

// How far back each sync reaches (INITIAL_SYNC_DAYS / ROUTINE_SYNC_DAYS) is
// decided server-side now — see syncWindowStart in api/_lib/syncPipeline.js.

/* ── Theme initializer (module-scope for useState lazy-init) ─────────── */
function getInitialTheme() {
  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'system';
}

/* ── App ─────────────────────────────────────────────────────────────── */
export default function App() {
  const [user,setUser]             = useState(null);
  const [installPrompt,setIP]      = useState(null);
  const [showInstall,setShowInstall] = useState(false);
  const [editTx, setEditTx]         = useState(null);
  const [authError, setAuthError] = useState('');
  // Firebase restores the session asynchronously, so `user` is null for the
  // first frames of every launch whether or not anyone is signed in. Rendering
  // Login on that is what flashes the sign-in screen at a signed-in user.
  // Nothing routes on `user` until this clears.
  const [authPending, setAuthPending] = useState(true);

  const [theme, setTheme] = useState(getInitialTheme);
  const [sysTick, setSysTick] = useState(0);
  const [bankSyncing, setBankSyncing]         = useState(false);
  const [accountPickerData,    setAccountPickerData]    = useState(null); // { accounts, connection }
  const [recategorizingAI,    setRecategorizingAI]    = useState(false);
  const [recategorizingAccountId, setRecategorizingAccountId] = useState(null);
  const [uploadingProfilePhoto, setUploadingProfilePhoto] = useState(false);
  const [toastQueue,          setToastQueue]          = useState([]); // [{ id, type, message }]
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Firebase data layer — all onValue() subscriptions live in this hook.
  const {
    transactions,
    envelopes,
    envelopeMeta,
    goals,
    scenarios,
    plannedSeries,
    seriesLifecycle,
    recurringDismissed,
    seriesNames,
    appCategories,
    appCategoryMeta,
    accounts,
    bankConnections,
    runwayFloor, setRunwayFloor,
    profilePhotoUrl, setProfilePhotoUrl,
    configLoaded,
    dataReady,
  } = useFirebaseData(user?.uid ?? null);

  // Keep the synchronous getCategoryMeta() lookups in sync with the user's
  // saved per-category icon/color customizations before any child renders.
  setCategoryMetaOverrides(appCategoryMeta);

  const toastIdRef = useRef(0);
  const userRef = useRef(null);
  const hasAutoSynced = useRef(false);
  const lastAutoSyncRef = useRef(0);
  const enrollmentsRef = useRef([]);
  // Background syncs only touch live Plaid connections — records from the
  // retired providers can never fetch again and would just produce noise.
  enrollmentsRef.current = bankConnections.filter(e => e.provider === 'plaid');
  const bankSyncingRef = useRef(false);
  bankSyncingRef.current = bankSyncing;
  const hasBackfilled = useRef(false);
  const hasRepaymentMigrated = useRef(false);
  // Transfer pairing is computed further down (it needs the account list); the
  // card-type migration above runs before that in source order and reads the
  // latest value through this ref.
  const transferIdsRef = useRef(new Set());

  // Enqueue a transient notification. Each toast has a unique id so the
  // queue component can dismiss it precisely without clearing others.
  const showToast = (type, message) => {
    const id = ++toastIdRef.current;
    setToastQueue((q) => [...q, { id, type, message }]);
  };

  const dismissToast = (id) => {
    setToastQueue((q) => q.filter((t) => t.id !== id));
  };

  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const pastTransactions = transactions.filter(t => new Date(t.date) <= today);

  const saveEnvelope = (category, limit) => {
    const parsed = parseFloat(limit);
    if (isNaN(parsed) || parsed < 0) return;
    set(ref(database, `users/${user.uid}/envelopes/${category.replace(/[^a-zA-Z0-9]/g, '_')}`), parsed);
  };
  
  const deleteEnvelope = (category) => {
    const key = category.replace(/[^a-zA-Z0-9]/g, '_');
    const updates = {
      [`users/${user.uid}/envelopes/${key}`]: null,
      [`users/${user.uid}/envelopeMeta/${key}`]: null,
    };
    update(ref(database), updates);
  };

  const setEnvelopeRollover = (category, rollover) => {
    if (!user) return;
    const key = category.replace(/[^a-zA-Z0-9]/g, '_');
    if (rollover) {
      set(ref(database, `users/${user.uid}/envelopeMeta/${key}`), { rollover: true });
    } else {
      remove(ref(database, `users/${user.uid}/envelopeMeta/${key}`));
    }
  };

  const bulkSetEnvelopeLimits = (changes) => {
    if (!user) return;
    const updates = {};
    Object.entries(changes).forEach(([category, limit]) => {
      const parsed = parseFloat(limit);
      if (Number.isNaN(parsed) || parsed < 0) return;
      const key = category.replace(/[^a-zA-Z0-9]/g, '_');
      updates[`users/${user.uid}/envelopes/${key}`] = parsed;
    });
    if (Object.keys(updates).length > 0) update(ref(database), updates);
  };

  const saveGoal = (goal) => {
    if (!user) return;
    const payload = {
      name: String(goal.name || '').trim(),
      target: Number(goal.target) || 0,
      startingAmount: Number(goal.startingAmount) || 0,
      startDate: goal.startDate || new Date().toISOString().slice(0, 10),
      targetDate: goal.targetDate || '',
      type: goal.type === 'payoff' ? 'payoff' : 'savings',
      linkedCategory: goal.linkedCategory || null,
      archived: !!goal.archived,
    };
    if (!payload.name || payload.target <= 0) return;
    if (goal.id) {
      set(ref(database, `users/${user.uid}/goals/${goal.id}`), payload);
    } else {
      push(ref(database, `users/${user.uid}/goals`), payload);
    }
  };

  const deleteGoal = (id) => {
    if (!user || !id) return;
    remove(ref(database, `users/${user.uid}/goals/${id}`));
  };

  const archiveGoal = (id, archived = true) => {
    if (!user || !id) return;
    update(ref(database, `users/${user.uid}/goals/${id}`), { archived });
  };

  const saveScenario = (scenario) => {
    if (!user) return;
    const payload = {
      name: String(scenario.name || '').trim() || 'Untitled scenario',
      enabled: !!scenario.enabled,
      deltas: Array.isArray(scenario.deltas) ? scenario.deltas : [],
    };
    if (scenario.id) {
      set(ref(database, `users/${user.uid}/scenarios/${scenario.id}`), payload);
    } else {
      push(ref(database, `users/${user.uid}/scenarios`), payload);
    }
  };

  const deleteScenario = (id) => {
    if (!user || !id) return;
    remove(ref(database, `users/${user.uid}/scenarios/${id}`))
      .catch((err) => showToast('error', `Couldn't delete scenario: ${err.message}`));
  };

  const toggleScenarioEnabled = (id, enabled) => {
    if (!user || !id) return;
    update(ref(database, `users/${user.uid}/scenarios/${id}`), { enabled: !!enabled });
  };

  // A recurring commitment entered before its first charge. Never written as a
  // transaction — see lib/forecasting/planned.js for why it stays separate.
  const savePlanned = (plan) => {
    if (!user || !plan) return;
    const payload = {
      name: String(plan.name || '').trim(),
      amount: Math.abs(Number(plan.amount)) || 0,
      type: plan.type === 'income' ? 'income' : 'expense',
      recurrenceType: plan.recurrenceType || 'monthly',
      firstDate: plan.firstDate || null,
      category: plan.category || 'Other',
      accountId: plan.accountId || null,
      matchHint: String(plan.matchHint || '').trim() || null,
    };
    if (!payload.name || !payload.amount || !payload.firstDate) return;
    if (plan.id) {
      update(ref(database, `users/${user.uid}/plannedSeries/${plan.id}`), payload);
    } else {
      push(ref(database, `users/${user.uid}/plannedSeries`), {
        ...payload,
        status: 'active',
        createdAt: new Date().toISOString(),
      });
    }
  };

  const deletePlanned = (id) => {
    if (!user || !id) return;
    remove(ref(database, `users/${user.uid}/plannedSeries/${id}`));
  };

  // The user confirmed a suggested first charge for a plan.
  const linkPlanToTransaction = (plan, tx) => {
    if (!user || !plan?.id || !tx?.id) return;
    update(ref(database), planLinkWrites(user.uid, plan, tx, { seriesNames }));
  };

  // "Not this one" — remembered on the plan so it's never suggested again.
  const dismissPlanMatch = (planId, txId) => {
    if (!user || !planId || !txId) return;
    set(ref(database, `users/${user.uid}/plannedSeries/${planId}/dismissedTxIds/${txId}`), true);
  };

  // Answers to "haven't seen this charge" — see lib/forecasting/missed.js.
  const lifecyclePath = (key) => `users/${user.uid}/seriesLifecycle/${firebaseSafeKey(key)}`;

  const linkMissedCharge = (missed, tx) => {
    if (!user || !missed?.key || !tx?.id) return;
    update(ref(database), missedLinkWrites(user.uid, missed, tx));
  };

  const skipMissedCharge = (missed) => {
    if (!user || !missed?.key) return;
    update(ref(database), {
      [`${lifecyclePath(missed.key)}/seriesKey`]: missed.key,
      [`${lifecyclePath(missed.key)}/skips/${lifecycleDay(missed.expectedDate)}`]: true,
    });
  };

  // Recorded as the last charge's date, so a later charge revives the series.
  const endSeries = (missed) => {
    if (!user || !missed?.key) return;
    update(ref(database, lifecyclePath(missed.key)), { seriesKey: missed.key, endedAt: lifecycleDay(missed.lastDate) });
  };

  const resumeSeries = (key) => {
    if (!user || !key) return;
    update(ref(database, lifecyclePath(key)), { endedAt: null });
  };

  const dismissMissedMatch = (key, txId) => {
    if (!user || !key || !txId) return;
    set(ref(database, `${lifecyclePath(key)}/dismissedTxIds/${txId}`), true);
  };

  // Marks all transactions matching a candidate as recurring with the
  // detected cadence. One batched write.
  const confirmRecurringCandidate = (candidate) => {
    if (!user || !candidate || !Array.isArray(candidate.ids)) return;
    const updates = {};
    candidate.ids.forEach((id) => {
      updates[`users/${user.uid}/transactions/${id}/isRecurring`] = true;
      updates[`users/${user.uid}/transactions/${id}/recurrenceType`] = candidate.recurrenceType;
    });
    if (Object.keys(updates).length > 0) update(ref(database), updates);
  };

  const dismissRecurringCandidate = (seriesKey) => {
    if (!user || !seriesKey) return;
    set(ref(database, `users/${user.uid}/recurringDismissed/${firebaseSafeKey(seriesKey)}`), true);
  };

  const addCategory = (name) => {
    const trimmed = name.trim();
    if (!trimmed || appCategories.includes(trimmed)) return;
    set(ref(database, `users/${user.uid}/categories`), [...appCategories, trimmed]);
  };

  const removeCategory = (name) => {
    if (!window.confirm(`Delete category "${name}"? Uncategorized expenses will be moved to "Other".`)) return;
    const newCats = appCategories.filter(c => c !== name);
    set(ref(database, `users/${user.uid}/categories`), newCats);
    
    const updates = {};
    transactions.forEach(tx => {
      if (tx.category === name) {
        updates[`users/${user.uid}/transactions/${tx.id}/category`] = 'Other';
      }
    });
    const removeKey = name.replace(/[^a-zA-Z0-9]/g, '_');
    updates[`users/${user.uid}/envelopes/${removeKey}`] = null;
    updates[`users/${user.uid}/envelopeMeta/${removeKey}`] = null;
    if (appCategoryMeta[firebaseSafeKey(name)]) updates[`users/${user.uid}/categoryMeta/${firebaseSafeKey(name)}`] = null;

    if (Object.keys(updates).length > 0) update(ref(database), updates);
  };

  // Persist a custom icon/color for a category. Passing `null` for a field
  // (or an empty object) clears that customization and falls back to defaults.
  const setCategoryAppearance = (name, { icon, tint } = {}) => {
    if (!user || !name) return;
    const safeName = firebaseSafeKey(name);
    const next = { ...(appCategoryMeta[safeName] || {}) };
    if (icon) next.icon = icon; else delete next.icon;
    if (tint) next.tint = tint; else delete next.tint;
    const path = `users/${user.uid}/categoryMeta/${safeName}`;
    set(ref(database, path), Object.keys(next).length ? next : null)
      .catch((err) => showToast('error', `Couldn't save category appearance: ${err.message}`));
  };

  const renameCategory = (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName || appCategories.includes(trimmed)) {
      return;
    }
    const newCats = appCategories.map(c => c === oldName ? trimmed : c);
    set(ref(database, `users/${user.uid}/categories`), newCats);

    const updates = {};
    transactions.forEach(tx => {
      if (tx.category === oldName) {
        updates[`users/${user.uid}/transactions/${tx.id}/category`] = trimmed;
      }
    });
    
    const oldKey = oldName.replace(/[^a-zA-Z0-9]/g, '_');
    const newKey = trimmed.replace(/[^a-zA-Z0-9]/g, '_');
    if (envelopes[oldKey] !== undefined) {
      updates[`users/${user.uid}/envelopes/${newKey}`] = envelopes[oldKey];
      updates[`users/${user.uid}/envelopes/${oldKey}`] = null;
    }
    if (envelopeMeta[oldKey]) {
      updates[`users/${user.uid}/envelopeMeta/${newKey}`] = envelopeMeta[oldKey];
      updates[`users/${user.uid}/envelopeMeta/${oldKey}`] = null;
    }
    if (appCategoryMeta[firebaseSafeKey(oldName)]) {
      updates[`users/${user.uid}/categoryMeta/${firebaseSafeKey(trimmed)}`] = appCategoryMeta[firebaseSafeKey(oldName)];
      updates[`users/${user.uid}/categoryMeta/${firebaseSafeKey(oldName)}`] = null;
    }

    if (Object.keys(updates).length > 0) update(ref(database), updates);
  };

  const saveRunwayFloor = (value) => {
    setRunwayFloor(value);
    set(ref(database, `users/${userRef.current?.uid}/runwayFloor`), value);
  };

  // Persist a Plaid connection. `selectedAccounts` is the array of (normalized)
  // Plaid account objects picked by the user, each carrying the type the user
  // confirmed in the picker. The record lives in the historical
  // `bankConnections` node (kept so existing data still loads; the whole
  // sync pipeline keys off that node) distinguished by `provider: 'plaid'`.
  // The `itemId` is all the client needs — the Plaid access_token lives
  // server-side. One Plaid item spans one institution.
  //
  // Async because the caller kicks off a sync straight afterwards, and that
  // sync runs on the server: it reads this enrollment back out of the database
  // rather than being handed the object, so the writes have to have landed
  // before it starts or the connection looks like it doesn't exist yet.
  const savePlaidConnection = async (itemId, institutionName, selectedAccounts) => {
    const uid = userRef.current?.uid;
    if (!uid || !itemId || !selectedAccounts?.length) return null;
    const institution = institutionName
      || [...new Set(selectedAccounts.map(a => a.institution?.name).filter(Boolean))].join(' · ')
      || 'Bank';
    const accountIds = selectedAccounts.map(a => a.id);
    // The picker's confirmed types are stored and passed back to the server on
    // every sync — they drive expense/repayment classification and balance
    // sign handling for any account Plaid couldn't type on its own.
    const accountTypes = Object.fromEntries(selectedAccounts.map(a => [a.id, a.type || 'depository']));
    const accountLabel = selectedAccounts.length === 1
      ? selectedAccounts[0].name
      : selectedAccounts.map(a => a.name).join(' · ');
    const key = push(ref(database, `users/${uid}/${CONNECTIONS_NODE}`)).key;
    const enrollment = {
      provider:     'plaid',
      itemId,
      institution,
      enrollmentId: key,
      accountId:    accountIds[0],
      accountIds,
      accountTypes,
      accountLabel,
      lastSynced:   null,
    };
    await Promise.all([
      set(ref(database, `users/${uid}/${CONNECTIONS_NODE}/${key}`), enrollment),
      ...selectedAccounts.map(account =>
        set(ref(database, `users/${uid}/accounts/${account.id}`), {
          name:        account.name,
          institution: account.institution?.name || institution,
          // 'depository' (checking/savings) vs 'credit' (cards).
          type:        account.type || 'depository',
          subtype:     account.subtype || '',
          lastFour:    account.last_four || '',
          enrollmentId: key,
          createdAt:   new Date().toISOString(),
        })
      ),
    ]);
    return { ...enrollment, id: key };
  };
  
  // Migration path for connections orphaned when the app moved to Plaid as its
  // only bank provider. Drop the dead enrollment record and its account docs (so stale balances stop counting
  // toward net worth) but KEEP the imported transactions — they render from
  // their own stored accountName/description fields.
  const removeLegacyConnection = (enrollmentId) => {
    const uid = userRef.current?.uid;
    if (!uid) return;
    remove(ref(database, `users/${uid}/${CONNECTIONS_NODE}/${enrollmentId}`));
    accounts
      .filter(a => a.enrollmentId === enrollmentId)
      .forEach(a => remove(ref(database, `users/${uid}/accounts/${a.id}`)));
    showToast('success', 'Old connection removed — transaction history kept');
  };

  // Fully delete a bank connection: for a live Plaid item, also tell the
  // server to invalidate the item with Plaid (stops it counting toward the
  // connection limit / billing) and drop the stored access_token.
  const deleteBankConnection = async (enrollmentId) => {
    const uid = userRef.current?.uid;
    if (!uid) return;
    const enr = bankConnections.find(e => e.id === enrollmentId);
    if (enr?.provider === 'plaid' && enr.itemId) {
      try {
        const idToken = await auth.currentUser?.getIdToken();
        await fetch('/api/plaid-remove', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(idToken && { Authorization: `Bearer ${idToken}` }) },
          body: JSON.stringify({ itemId: enr.itemId }),
        });
      } catch (e) {
        console.warn('Could not remove Plaid item server-side:', e.message);
      }
    }
    remove(ref(database, `users/${uid}/${CONNECTIONS_NODE}/${enrollmentId}`));
    accounts
      .filter(a => a.enrollmentId === enrollmentId)
      .forEach(a => remove(ref(database, `users/${uid}/accounts/${a.id}`)));
    transactions
      .filter(tx => tx[BANK_TX_CONNECTION_ID] === enrollmentId)
      .forEach(tx => delTx(tx.id));
  };

  // Manual (unlinked) cash accounts: no sync, no institution — just a name
  // and a balance the user sets and edits by hand.
  const addManualAccount = (name, startingBalance = 0) => {
    const uid = userRef.current?.uid;
    const trimmed = String(name || '').trim();
    if (!uid || !trimmed) return;
    push(ref(database, `users/${uid}/accounts`), {
      name: trimmed,
      type: 'manual',
      balance: { ledger: Number(startingBalance) || 0 },
      balanceAsOf: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    });
  };

  const renameManualAccount = (id, name) => {
    const uid = userRef.current?.uid;
    const trimmed = String(name || '').trim();
    if (!uid || !id || !trimmed) return;
    update(ref(database, `users/${uid}/accounts/${id}`), { name: trimmed });
  };

  const editManualAccountBalance = (id, newBalance) => {
    const uid = userRef.current?.uid;
    if (!uid || !id) return;
    update(ref(database, `users/${uid}/accounts/${id}`), {
      balance: { ledger: Number(newBalance) || 0 },
      balanceAsOf: new Date().toISOString(),
    });
  };

  const deleteManualAccount = (id) => {
    const uid = userRef.current?.uid;
    if (!uid || !id) return;
    remove(ref(database, `users/${uid}/accounts/${id}`));
  };

  // Lets a depository or manual account opt out of the headline cash
  // balance/forecast (e.g. a savings account you don't want counted toward
  // spendable cash) without removing or unlinking it.
  const setAccountIncludedInBalance = (id, included) => {
    const uid = userRef.current?.uid;
    if (!uid || !id) return;
    update(ref(database, `users/${uid}/accounts/${id}`), { includeInBalance: included });
  };

  // A whole backlog of transactions can't be categorized in one serverless
  // invocation — large requests blow past Vercel's wall-clock limit and 504.
  // Send transactions in bounded chunks (each maps to a single server-side
  // Gemini batch) so every request finishes well within budget. Chunks run
  // sequentially to avoid tripping Gemini's rate limits. Returns the combined
  // results plus `partial` = true if any chunk was rate-limited or failed (so
  // its transactions fall back to "Other" and the user can retry).
  const CATEGORIZE_CHUNK_SIZE = 100;

  const categorizeInChunks = async (payload, categories) => {
    const results = [];
    let partial = false;
    for (let i = 0; i < payload.length; i += CATEGORIZE_CHUNK_SIZE) {
      const chunk = payload.slice(i, i + CATEGORIZE_CHUNK_SIZE);
      try {
        const idToken = await auth.currentUser?.getIdToken();
        const res = await fetch('/api/auto-categorize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken && { Authorization: `Bearer ${idToken}` }),
          },
          body: JSON.stringify({ transactions: chunk, categories }),
        });
        if (!res.ok) { partial = true; continue; }
        const data = await res.json();
        if (Array.isArray(data.results)) results.push(...data.results);
        if (data.partial) partial = true;
      } catch (e) {
        console.warn('Auto-categorize chunk failed, falling back to Other:', e.message);
        partial = true;
      }
    }
    return { results, partial };
  };

  /* ── Bank sync ────────────────────────────────────────────────────────
   * The sync pipeline runs SERVER-SIDE (api/_lib/syncPipeline.js, reached
   * through /api/plaid-sync). The browser used to run its own copy of it and
   * write transactions directly; that made two unlocked writers for the same
   * Plaid transaction ids — the browser and the webhook-driven server sync —
   * and any overlap produced duplicate rows, because each read a pre-sync
   * snapshot, found nothing under the provider id, and pushed its own. The
   * server's advisory `syncLock` now covers every path.
   *
   * Nothing here reads transaction state, which also retires a second source
   * of duplicates: the background timer below captured `transactions` in a
   * closure that was never refreshed, so its dedup map went stale the moment
   * a server-side sync imported anything.
   *
   * Results arrive over the live Firebase listeners, same as a sync started
   * on another device.
   */
  // `live` asks the server for the bank's balance right now — a paid Plaid
  // call per bank — instead of Plaid's cached one. Only for syncs the user
  // pressed; automatic ones stay on the cached balance, which also matches
  // the transactions it arrives with.
  const runBankSync = async ({ itemId = null, toast = true, live = false } = {}) => {
    const uid = userRef.current?.uid;
    if (!uid) return { ok: true, added: 0 };
    setBankSyncing(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/plaid-sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken && { Authorization: `Bearer ${idToken}` }),
        },
        body: JSON.stringify({ ...(itemId && { itemId }), ...(live && { live: true }) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `sync failed (${res.status})`);

      const {
        ok = true, locked = false, partial = false, errors = [],
        added = 0, updated = 0, skipped = 0, dupes = 0,
      } = data;

      if (toast) {
        if (locked) {
          // Another run (webhook, phone, second tab) holds the lock. Its writes
          // land on the listeners, so this is a no-op rather than a failure.
          showToast('success', 'Already syncing — new transactions will appear shortly');
        } else if (!ok) {
          showToast('error', `Sync failed: ${errors[0] || 'your bank could not be reached'}`);
        } else if (errors.length) {
          // Synced, but a bank reported something alongside the data it did return.
          showToast('error', `Synced ${added} new, but your bank reported: ${errors[0]}`);
        } else if (partial) {
          showToast('error', `Synced ${added} new, but categorization was rate-limited. Try again in a minute.`);
        } else {
          const parts = [`${added} new`];
          if (updated) parts.push(`${updated} updated`);
          if (skipped) parts.push(`${skipped} manual kept`);
          if (dupes)   parts.push(`${dupes} dupes skipped`);
          showToast('success', `Synced — ${parts.join(', ')}`);
        }
      }
      return { ok, locked, added, updated, skipped, dupes, partial, error: ok ? undefined : errors[0] };
    } catch (err) {
      console.error('Bank sync error:', err);
      if (toast) showToast('error', `Sync failed: ${err.message}`);
      return { ok: false, added: 0, error: err.message };
    } finally {
      setBankSyncing(false);
    }
  };

  // Single-enrollment entry point (Settings "Sync" button, post-connect sync).
  // Connections from a retired provider can never fetch again; the server
  // skips them silently, so the truthful error stays here.
  const syncBankConnection = (enrollment, { live = false } = {}) => {
    if (enrollment?.provider !== 'plaid') {
      showToast('error', `${enrollment?.institution || 'Bank'}: this connection uses a retired provider — remove it and re-link the bank with Plaid`);
      return Promise.resolve({ ok: false, added: 0 });
    }
    return runBankSync({ itemId: enrollment.itemId, live });
  };

  // Only Plaid connections are syncable; records from a retired provider are
  // kept visible (so the user can migrate off them) but never fetched.
  const syncableEnrollments = bankConnections.filter(e => e.provider === 'plaid');

  const handleSyncAll = () => {
    if (bankSyncing || syncableEnrollments.length === 0) return;
    return runBankSync({ live: true });
  };

  // Freshness for the sidebar readout. The OLDEST successful sync across live
  // connections is the honest figure — "updated 2m ago" would be a lie if one
  // of three banks last landed a week back.
  const lastSyncedAt = (() => {
    const times = syncableEnrollments
      .map(e => (e.lastSynced ? new Date(e.lastSynced).getTime() : NaN))
      .filter(t => !isNaN(t));
    return times.length === syncableEnrollments.length && times.length
      ? new Date(Math.min(...times))
      : null;
  })();

  const applyCategorization = async (targets, { emptyMessage, successLabel = 'Re-categorized' } = {}) => {
    const uid = userRef.current?.uid;
    if (!uid) return;
    if (targets.length === 0) {
      showToast('success', emptyMessage || 'Nothing to categorize — all set');
      return;
    }

    const payload = targets.map(t => ({
      id: t.id,
      description: t.description,
      amount: t.amount,
      type: t.type,
    }));
    const { map, needsAI: remaining } = resolveCategories(payload, {
      categories: appCategories,
      pastTransactions: transactions,
    });
    let partial = false;
    if (remaining.length > 0) {
      const ai = await categorizeInChunks(remaining, appCategories);
      partial = ai.partial;
      ai.results.forEach(r => { map[r.id] = r.category; });
    }
    const results = payload.map(t => ({ id: t.id, category: map[t.id] || 'Other' }));

    const changes = results.filter((r) => {
      const tx = targets.find(t => t.id === r.id);
      return tx && r.category !== tx.category;
    });
    await Promise.all(
      changes.map(r => set(ref(database, `users/${uid}/transactions/${r.id}/category`), r.category))
    );
    if (partial) {
      showToast('error', `Categorized ${changes.length} — some were skipped (Gemini rate limit). Try again in a minute.`);
    } else {
      showToast('success', changes.length
        ? `${successLabel} ${changes.length} transaction${changes.length === 1 ? '' : 's'}`
        : 'No category changes suggested');
    }
  };

  const reCategorizeAll = async () => {
    if (recategorizingAI || recategorizingAccountId) return;
    setRecategorizingAI(true);
    try {
      await applyCategorization(
        transactions.filter(t => t.category === 'Other' && !t.manualCategory),
      );
    } catch (err) {
      console.error('Re-categorize error:', err);
      showToast('error', `Re-categorize failed: ${err.message}`);
    } finally {
      setRecategorizingAI(false);
    }
  };

  const reCategorizeAccount = async (accountId) => {
    if (!accountId || recategorizingAI || recategorizingAccountId) return;
    setRecategorizingAccountId(accountId);
    try {
      await applyCategorization(
        transactions.filter(t => t.accountId === accountId && !t.manualCategory && t.type !== 'repayment'),
        {
          emptyMessage: 'No transactions to categorize for this account',
          successLabel: 'Categorized',
        },
      );
    } catch (err) {
      console.error('Account re-categorize error:', err);
      showToast('error', `Re-categorize failed: ${err.message}`);
    } finally {
      setRecategorizingAccountId(null);
    }
  };

  useEffect(() => {
    // Only auto-sync once config is loaded from Firebase
    if (configLoaded && !hasAutoSynced.current && enrollmentsRef.current.length > 0) {
      hasAutoSynced.current = true;
      lastAutoSyncRef.current = Date.now();
      // Background sync on load: server-side, no toast.
      runBankSync({ toast: false });
    }
  }, [configLoaded, bankConnections]); // eslint-disable-line react-hooks/exhaustive-deps

  // Long-lived tabs (a PWA left open) never re-hit the on-load sync, so
  // without this the data quietly ages until the user reloads. Re-sync in
  // the background every AUTO_SYNC_INTERVAL_MS, and when the tab regains
  // visibility past that window. Refs keep the timer callback reading
  // current state without re-registering listeners every render — and since
  // the sync itself now runs server-side, the only state this closure reads
  // is behind those refs.
  useEffect(() => {
    if (!configLoaded) return;
    const autoSync = () => {
      if (bankSyncingRef.current) return;
      if (enrollmentsRef.current.length === 0) return;
      if (Date.now() - lastAutoSyncRef.current < AUTO_SYNC_INTERVAL_MS) return;
      lastAutoSyncRef.current = Date.now();
      runBankSync({ toast: false });
    };
    const interval = setInterval(autoSync, AUTO_SYNC_CHECK_MS);
    const onVisibility = () => { if (!document.hidden) autoSync(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [configLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-shot backfill for data that predates multi-account support:
  //   1. stamp accountId onto existing bank-imported transactions
  //   2. create an accounts/{accountId} doc for LEGACY enrollments that lack
  //      one (default type 'depository' = cash, preserving old behavior).
  // Enrollments with an accountIds array are new-style: they write their own
  // account docs at connect time and must not be touched here — right after a
  // connect, this effect can run before the accounts snapshot arrives, and
  // backfilling would overwrite a just-created credit account as cash.
  useEffect(() => {
    if (!configLoaded || hasBackfilled.current || bankConnections.length === 0) return;
    const knownAccountIds = new Set(accounts.map(a => a.id));
    const legacyEnrollments = bankConnections.filter(enr => !Array.isArray(enr.accountIds));
    const enrollmentToAccount = {};
    legacyEnrollments.forEach(enr => {
      if (enr.id && enr.accountId) enrollmentToAccount[enr.id] = enr.accountId;
    });
    const needsTxBackfill = transactions.some(tx => !tx.accountId && tx[BANK_TX_CONNECTION_ID] && enrollmentToAccount[tx[BANK_TX_CONNECTION_ID]]);
    const needsAcctBackfill = legacyEnrollments.some(enr => enr.accountId && !knownAccountIds.has(enr.accountId));
    if (!needsTxBackfill && !needsAcctBackfill) return;
    hasBackfilled.current = true;
    const uid = userRef.current?.uid;
    if (!uid) return;
    const updates = {};
    transactions.forEach(tx => {
      if (!tx.accountId && tx[BANK_TX_CONNECTION_ID] && enrollmentToAccount[tx[BANK_TX_CONNECTION_ID]]) {
        updates[`users/${uid}/transactions/${tx.id}/accountId`] = enrollmentToAccount[tx[BANK_TX_CONNECTION_ID]];
      }
    });
    legacyEnrollments.forEach(enr => {
      if (enr.accountId && !knownAccountIds.has(enr.accountId)) {
        updates[`users/${uid}/accounts/${enr.accountId}`] = {
          name:        enr.accountLabel || enr.institution || 'Account',
          institution: enr.institution || 'Bank Account',
          type:        'depository',
          subtype:     '',
          enrollmentId: enr.id,
          createdAt:   new Date().toISOString(),
        };
      }
    });
    if (Object.keys(updates).length > 0) update(ref(database), updates);
  }, [configLoaded, bankConnections, transactions, accounts]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time fixup for money credited to a card that was stored under the
  // wrong type. Two generations of it:
  //
  //   'income'     — written before the repayment type existed at all.
  //   'repayment'  — written before refunds were told apart from payments, so
  //                  every merchant return was filed as a card payment.
  //
  // A payment says so in its descriptor ("PAYMENT THANK YOU", "AUTOPAY"), or
  // is provable from the cash-side leg transfer detection already paired it
  // with. Anything else credited to a card is a refund.
  useEffect(() => {
    if (!configLoaded || hasRepaymentMigrated.current || accounts.length === 0 || transactions.length === 0) return;
    const creditIds = new Set(accounts.filter(a => a.type === 'credit').map(a => a.id));
    if (creditIds.size === 0) return;
    const uid = userRef.current?.uid;
    if (!uid) return;
    const paired = transferIdsRef.current;
    const retypeOf = (tx) => {
      if (!creditIds.has(tx.accountId)) return null;
      if (looksLikeCardPayment(tx.description) || paired.has(tx.id)) {
        return tx.type === 'repayment' ? null : 'repayment';
      }
      return tx.type === 'refund' ? null : 'refund';
    };
    const updates = {};
    transactions.forEach(tx => {
      if (tx.type !== 'income' && tx.type !== 'repayment') return;
      const retyped = retypeOf(tx);
      if (retyped) updates[`users/${uid}/transactions/${tx.id}/type`] = retyped;
    });
    hasRepaymentMigrated.current = true;
    if (Object.keys(updates).length > 0) update(ref(database), updates);
  }, [configLoaded, accounts, transactions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Plaid connect flow ─────────────────────────────────────────────────
  // The user opens Plaid Link (a hosted widget that handles institution
  // search, bank login and MFA), picks a bank, and Link hands back a one-time
  // public_token. We exchange it server-side for a permanent access_token
  // (stored server-side, never sent here), load the item's accounts into the
  // existing picker, and save + sync the selection. Reconnecting an existing
  // connection reuses Link in "update mode" (openPlaidLink(itemId)).
  const [plaidLinkToken, setPlaidLinkToken]     = useState(null);
  const [plaidUpdateItemId, setPlaidUpdateItemId] = useState(null); // set → update/reconnect mode
  const [plaidConnecting, setPlaidConnecting]   = useState(false);
  const [connectionHelp, setConnectionHelp]     = useState(null);   // legacy-provider migration sheet

  // Fetch a Link token and mount <PlaidLink>, which opens Link once ready.
  const openPlaidLink = async (itemId = null) => {
    if (plaidConnecting) return;
    setPlaidConnecting(true);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/plaid-link-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(idToken && { Authorization: `Bearer ${idToken}` }) },
        body: JSON.stringify(itemId ? { itemId } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.linkToken) throw new Error(data.error || `Could not start Plaid (HTTP ${res.status})`);
      setPlaidUpdateItemId(itemId);
      setPlaidLinkToken(data.linkToken);
    } catch (e) {
      showToast('error', e.message);
      setPlaidConnecting(false);
    }
  };

  const handlePlaidExit = () => {
    setPlaidLinkToken(null);
    setPlaidUpdateItemId(null);
    setPlaidConnecting(false);
  };

  const handlePlaidSuccess = async (publicToken, metadata) => {
    const updatingItemId = plaidUpdateItemId;
    handlePlaidExit(); // tear down the Link launcher
    const uid = userRef.current?.uid;

    if (updatingItemId) {
      // Update mode (reconnect): the item is already linked — just clear the
      // disconnected flag and resync. No token exchange needed.
      const enr = bankConnections.find(e => e.itemId === updatingItemId);
      if (enr && uid) {
        update(ref(database, `users/${uid}/${CONNECTIONS_NODE}/${enr.id}`), {
          status: 'active', disconnectReason: null, statusUpdatedAt: new Date().toISOString(),
        });
        showToast('success', 'Reconnected — syncing…');
        syncBankConnection({ ...enr, status: 'active', disconnectReason: null });
      }
      return;
    }

    // New connection: exchange the public token, then open the account picker.
    try {
      const institution = metadata?.institution?.name || '';
      const idToken = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/plaid-exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(idToken && { Authorization: `Bearer ${idToken}` }) },
        body: JSON.stringify({ publicToken, institution }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.accounts?.length) throw new Error(data.error || 'Plaid returned no accounts for this bank.');
      setAccountPickerData({
        accounts: data.accounts,
        connection: { provider: 'plaid', itemId: data.itemId, institution: data.institution || institution },
      });
    } catch (e) {
      showToast('error', e.message);
    }
  };

  // "Reconnect": a live Plaid connection re-authenticates through Link update
  // mode; a connection from a retired provider can't be revived, so it opens
  // a sheet explaining how to move it to Plaid.
  const reconnectEnrollment = (enr) => {
    if (enr.provider === 'plaid') openPlaidLink(enr.itemId);
    else setConnectionHelp(enr);
  };

  // Connection health, derived per enrollment: records from a retired
  // provider are always broken (`defunct`); Plaid records are `disconnected` when they
  // need re-authentication, or `stale` after 3+ days without a successful sync.
  const STALE_SYNC_MS = 3 * 24 * 60 * 60 * 1000;
  const enrollmentIssues = bankConnections
    .map(enr => ({
      ...enr,
      defunct: enr.provider !== 'plaid',
      disconnected: enr.provider !== 'plaid' || enr.status === 'disconnected',
      stale: !!enr.lastSynced && (Date.now() - new Date(enr.lastSynced).getTime() > STALE_SYNC_MS),
    }))
    .filter(e => e.disconnected || e.stale);

  // Oldest balance timestamp among the accounts backing the headline number —
  // surfaced when it's old enough that the balance can't be trusted. For a
  // synced account this is when Plaid last reached the bank, not when we last
  // asked Plaid, so a connection Plaid has stopped refreshing shows its age.
  const balanceAsOf = useMemo(() => {
    const times = accounts
      .filter(a => a.type === 'depository' && a.includeInBalance !== false && a.balanceAsOf)
      .map(a => new Date(a.balanceAsOf).getTime())
      .filter(t => !isNaN(t));
    return times.length ? new Date(Math.min(...times)) : null;
  }, [accounts]);

  useEffect(() => {
    const el = document.documentElement;
    const resolved = theme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark')
      : theme;
    el.setAttribute('data-theme', resolved);
    el.classList.toggle('light', resolved === 'light');
    localStorage.setItem('theme', theme);
  }, [theme, sysTick]);

  // Auto-attach: when a non-recurring transaction matches a confirmed
  // recurring series (same desc + type + amount), flip it to recurring
  // automatically. Runs whenever transactions/user change; idempotent.
  useEffect(() => {
    if (!user || transactions.length === 0) return;
    const series = buildRecurringSeries(transactions);
    if (series.length === 0) return;
    // A descriptor the user linked to a series (series-aliases.js) matches as
    // that series, and the charge carries the key so it stays there.
    const aliasTo = seriesAliasMap(seriesLifecycle);
    const updates = {};
    transactions.forEach((t) => {
      if (t.isRecurring) return;
      const alias = aliasedSeriesKey(t, aliasTo);
      const match = matchExistingSeries(alias ? { ...t, seriesKey: alias } : t, series);
      if (!match) return;
      updates[`users/${user.uid}/transactions/${t.id}/isRecurring`] = true;
      updates[`users/${user.uid}/transactions/${t.id}/recurrenceType`] = match.recurrenceType;
      if (alias) updates[`users/${user.uid}/transactions/${t.id}/seriesKey`] = alias;
    });
    if (Object.keys(updates).length > 0) update(ref(database), updates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, transactions, seriesLifecycle]);

  // Auto-link: a planned series whose first charge has arrived becomes a real
  // series. Only the unambiguous fits link here (see plan-matching.js); the
  // rest are offered on the Recurring tab. The sync pipeline runs the same
  // match server-side, so this mostly catches manual and CSV entries.
  useEffect(() => {
    if (!user || transactions.length === 0 || plannedSeries.length === 0) return;
    const { links } = matchPlans(transactions, plannedSeries);
    if (links.length === 0) return;
    const updates = {};
    links.forEach(({ plan, tx }) => Object.assign(updates, planLinkWrites(user.uid, plan, tx, { seriesNames })));
    update(ref(database), updates);
    showToast('success', links.length === 1
      ? `${links[0].plan.name} matched its first charge`
      : `${links.length} planned series matched their first charges`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, transactions, plannedSeries]);

  // Listen for OS-level theme changes and mirror them when set to 'system'
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = () => {
      if (localStorage.getItem('theme') === 'system') {
        setSysTick(t => t + 1);
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(()=>{
    const h=e=>{ e.preventDefault(); setIP(e); setShowInstall(true); };
    window.addEventListener('beforeinstallprompt',h);
    return ()=>window.removeEventListener('beforeinstallprompt',h);
  },[]);

  // Firebase already persists the session (IndexedDB here, Keychain on iOS),
  // so a signed-in user should never see this screen twice. What used to end
  // the session was this gate failing closed: an allowlist read that could not
  // reach the network signed the user out, and signing out deletes the stored
  // credential. See lib/accessGate.js — only a completed read denies now, and
  // an unreachable one retries with the session left intact.
  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    let stopReconnectWatch = null;
    let splashTimer = null;

    // Long enough that a working connection almost always wins the race, short
    // enough that a request which will never answer — a captive portal, a
    // connection that accepts and then stalls — doesn't strand the launch on a
    // splash screen with no way forward.
    const SPLASH_HOLD_MS = 6000;

    const stopHoldingSplash = () => {
      if (splashTimer) { clearTimeout(splashTimer); splashTimer = null; }
      setAuthPending(false);
    };

    const clearPendingRetry = () => {
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      if (stopReconnectWatch) { stopReconnectWatch(); stopReconnectWatch = null; }
    };

    const admit = (u) => {
      setAuthError('');
      setUser(u);
      userRef.current = u;
      stopHoldingSplash();
    };

    const deny = async (message) => {
      await signOut(auth);
      setAuthError(message);
      setUser(null);
      userRef.current = null;
      stopHoldingSplash();
    };

    const scheduleRetry = (u, attempt) => {
      clearPendingRetry();
      const delay = Math.min(2000 * 2 ** (attempt - 1), 30000);
      retryTimer = setTimeout(() => { retryTimer = null; verify(u, attempt); }, delay);
      // Don't sit out the backoff if the network returns first.
      stopReconnectWatch = onReconnect(() => verify(u, attempt));
    };

    const verify = async (u, attempt) => {
      if (cancelled) return;
      const result = await resolveAccess(u.email);
      if (cancelled) return;

      if (result.status === 'allowed') {
        clearPendingRetry();
        admit(u);
        return;
      }
      if (result.status === 'denied') {
        clearPendingRetry();
        await deny('This app is private.');
        return;
      }

      // Unverified: keep the session and keep asking. A recent verified check
      // on this device carries the user through an offline launch; without one
      // they wait on the login screen, but their credential is still there, so
      // reconnecting signs them straight back in without a password.
      if (result.cachedGrant) {
        admit(u);
      } else if (!userRef.current) {
        setAuthError('Could not verify access — retrying automatically.');
        stopHoldingSplash();
      }
      scheduleRetry(u, attempt + 1);
    };

    const unsub = onAuthStateChanged(auth, u => {
      clearPendingRetry();
      if (u) {
        // A check this device already passed for this email opens the app now
        // rather than holding the splash for a network round trip, which is
        // longest on exactly the mobile connections where the wait is most
        // noticeable. The live check still runs behind it, and still signs out
        // an email that has since come off the list.
        if (hasValidGrant(u.email)) {
          admit(u);
        } else {
          splashTimer = setTimeout(() => { splashTimer = null; setAuthPending(false); }, SPLASH_HOLD_MS);
        }
        verify(u, 0);
      } else {
        setUser(null);
        userRef.current = null;
        stopHoldingSplash();
      }
    });

    return () => {
      cancelled = true;
      if (splashTimer) { clearTimeout(splashTimer); splashTimer = null; }
      clearPendingRetry();
      unsub();
    };
  }, []);

  const deleteSeriesInstances = (ids) => {
    ids.forEach(id => delTx(id));
  };
  
  // Renaming a series only sets a display label in seriesNames — instance
  // descriptions are never rewritten, because the series' matching identity
  // (seriesKeyOf = normalized description + type) is derived from them and
  // future bank charges arrive under the original description.
  const editSeriesInstances = (instances, updated) => {
    instances.forEach(tx => {
      const { id, ...rest } = tx;
      set(ref(database, `users/${user.uid}/transactions/${id}`), {
        ...rest,
        amount: updated.amount,
        category: updated.category,
        type: updated.type,
        recurrenceType: updated.recurrenceType,
      });
    });
    if (updated.displayName !== undefined && instances[0]) {
      const oldKey = seriesKeyOf(instances[0]);
      // Flipping expense/income changes the series key; move the label with it.
      // Spread the instance so a linked charge's explicit seriesKey is kept.
      const newKey = seriesKeyOf({ ...instances[0], type: updated.type });
      const name = updated.displayName.trim();
      const writes = {
        [`users/${user.uid}/seriesNames/${firebaseSafeKey(newKey)}`]: name || null,
      };
      if (oldKey !== newKey) writes[`users/${user.uid}/seriesNames/${firebaseSafeKey(oldKey)}`] = null;
      // A label saved under the pre-seriesDescKey identity would otherwise sit
      // there forever, shadowing nothing but never cleaned up.
      instances.forEach((tx) => {
        const legacy = firebaseSafeKey(legacySeriesKeyOf({ description: tx.description, type: updated.type }));
        if (legacy !== firebaseSafeKey(newKey)) writes[`users/${user.uid}/seriesNames/${legacy}`] = null;
      });
      update(ref(database), writes);
    }
  };

  const updateTx = (id, updated) => {
    const { id: _removed, ...rest } = updated;
    const original = transactions.find(t => t.id === id);
    // If this is a bank-synced transaction and the category changed, flag it as
    // manual so a later sync won't overwrite the user's choice.
    const manualCategory = BANK_TX_SOURCES.includes(original?.source) && updated.category !== original.category
      ? true
      : original?.manualCategory || false;
    set(ref(database, `users/${user.uid}/transactions/${id}`), { ...rest, manualCategory });
    setEditTx(null);
  };

  const delTx = id => remove(ref(database,`users/${user.uid}/transactions/${id}`));
  
  // ── Multi-account ledgers ──────────────────────────────────────────────
  // Accounts are either cash (checking/savings, provider type 'depository') or
  // credit (cards, type 'credit'). The headline balance is a CASH number —
  // money actually in the bank — so credit-card activity is excluded from it
  // and surfaced separately. Spending/income stats and the forecast use a
  // SPENDING lens: every account counts, but inter-account transfers (e.g. a
  // credit-card payment) are removed so they don't double-count the purchases
  // they pay off. With no credit accounts and no transfers (the single-account
  // case) every filter below collapses to "all transactions", leaving the
  // numbers identical to before multi-account support.
  const creditAccountIds = useMemo(
    () => new Set(accounts.filter(a => a.type === 'credit').map(a => a.id)),
    [accounts]
  );
  const transferPairs = useMemo(() => detectTransferPairs(transactions), [transactions]);
  const transferIds = transferPairs.ids;
  transferIdsRef.current = transferIds;
  const isCashTx     = (t) => !creditAccountIds.has(t.accountId);
  const isSpendingTx = (t) => !transferIds.has(t.id) && t.type !== 'repayment';

  // Headline cash balance = live bank balance summed across depository
  // accounts, plus manually-entered cash accounts. Uses `available` (spendable
  // money, pending holds netted out) via accountDisplayBalance, which falls
  // back to `ledger` for manual accounts. Accounts explicitly toggled off
  // (includeInBalance === false) are counted/listed elsewhere but excluded here.
  const balance = accounts
    .filter(a => (a.type === 'depository' || a.type === 'manual') && a.includeInBalance !== false)
    .reduce((s, a) => s + accountDisplayBalance(a), 0);

  // Per-card summaries (cardSummaries) are built further down, once the
  // in-flight payment amounts they're annotated with are known.

  const txInMonth = (list, ref) => list.filter(t => {
    const d = new Date(t.date);
    return d.getMonth() === ref.getMonth() && d.getFullYear() === ref.getFullYear();
  });
  const priorMonthOf = (ref) => new Date(ref.getFullYear(), ref.getMonth() - 1, 1);

  const monthlyTransactions = txInMonth(pastTransactions, selectedMonth);
  const priorMonthRef = priorMonthOf(selectedMonth);
  const priorMonthTransactions = txInMonth(pastTransactions, priorMonthRef);

  // The month the picker is parked on may not be this one. Today has no picker
  // — it is anchored to now by definition — so anything it renders has to be
  // built from the current month, not the selected one.
  const currentMonthRef = new Date(today.getFullYear(), today.getMonth(), 1);
  const isViewingCurrentMonth =
    selectedMonth.getMonth() === currentMonthRef.getMonth() &&
    selectedMonth.getFullYear() === currentMonthRef.getFullYear();

  // Budgets use the same spending lens as the headline income/expense stats:
  // drop inter-account transfers and credit-card repayments so they don't count
  // against envelopes (a card payment isn't spending against a budget).
  const monthlySpendingTx = monthlyTransactions.filter(isSpendingTx);
  const priorMonthSpendingTx = priorMonthTransactions.filter(isSpendingTx);

  // Spending lens: count purchases across all accounts, drop transfers. A
  // refund nets against what was spent rather than counting as income — the
  // money is a purchase coming undone, not something earned.
  const income   = monthlyTransactions.filter(t => isSpendingTx(t) && t.type === 'income').reduce((s,t) => s + t.amount, 0);
  const expenses = monthlyTransactions.filter(isSpendingTx).reduce((s,t) => s + spendDelta(t), 0);

  const spendingTransactions = transactions.filter(isSpendingTx);
  // Series the user said have ended: no future occurrences in any projector.
  const endedKeys = useMemo(
    () => endedSeriesKeys(transactions, seriesLifecycle),
    [transactions, seriesLifecycle]
  );

  const projection = adaptProjection(
    buildProjection(balance, spendingTransactions, 12, { goals, planned: plannedSeries, excludeSeriesKeys: endedKeys })
  );

  // Transfers with one leg still settling. Only the cash-affecting direction
  // (card credited, checking not yet debited) reaches the ledger.
  const inFlight = useMemo(
    () => detectInFlightTransfers(transactions, accounts, { pairs: transferPairs }),
    [transactions, accounts, transferPairs]
  );

  // The card-affecting half of the in-flight set: cash has already left the
  // bank but the card hasn't been credited, so its owed figure reads high.
  // Annotates the card row; the balance shown stays exactly what Plaid reported.
  const pendingCardPayments = useMemo(() => {
    const out = {};
    inFlight
      .filter((f) => f.affects === 'card')
      .forEach((f) => { out[f.missingAccountId] = (out[f.missingAccountId] || 0) + f.amount; });
    return out;
  }, [inFlight]);

  // Payments that have really moved but whose two legs haven't been matched to
  // each other yet. buildPaymentRoutes counts payments from matched pairs, so
  // these are invisible to it and its coverage ratio reads the hole as a
  // payment never made — demoting a payoff user to a revolver, and switching
  // off the balance-sizing precisely while a payment is in flight.
  const unpairedCardPayments = useMemo(() => {
    const out = {};
    inFlight
      .filter((f) => !f.paired && f.cardAccountId)
      .forEach((f) => { out[f.cardAccountId] = (out[f.cardAccountId] || 0) + f.amount; });
    return out;
  }, [inFlight]);

  // Upcoming card payments, sized from each card's live owed balance rather
  // than the mean of past payments — but only for cards the user demonstrably
  // pays off in full. See card-payments.js for why the mean is the wrong model
  // there and why it's the wrong fix everywhere else.
  //
  // Fed the in-flight amounts above, because a card that's already been paid
  // still reports the full balance until the credit posts. Sizing the next
  // payment off that would bill the user twice for the same money — the cash
  // balance the runway starts from has already lost it.
  const cardPayments = useMemo(
    () => projectCardPayments(transactions, accounts, {
      horizonDays: 60,
      pairs: transferPairs,
      inFlightByCard: pendingCardPayments,
      unpairedPaymentsByCard: unpairedCardPayments,
    }),
    [transactions, accounts, transferPairs, pendingCardPayments, unpairedCardPayments]
  );

  // Amount owed per card = the card's live ledger balance. Plaid reports a
  // credit ledger as a positive amount owed; if a real response shows
  // it negative, negate here so `owed` stays positive-when-owed (FocusShell
  // colors `owed > 0` as a debt).
  //
  // `owed` stays exactly what the bank reported, so the accounts screen keeps
  // agreeing with the bank's own app. `pendingPayment` rides alongside it for
  // the places that would otherwise mislead: cash has already left for that
  // amount, so a total built from `owed` alone reads as if the user is deeper
  // in debt than they are.
  const cardSummaries = useMemo(
    () => accounts
      .filter(a => a.type === 'credit')
      .map(a => {
        const card = { ...a, owed: Number(a.balance?.ledger) || 0 };
        const pending = pendingCardPayments[a.id];
        return pending ? { ...card, pendingPayment: pending } : card;
      }),
    [accounts, pendingCardPayments]
  );

  // Near-term cash runway. Deliberately uses the CASH lens, not the spending
  // lens above: a credit-card purchase doesn't move cash on the purchase date,
  // but the card *payment* out of checking does. Feeding it spendingTransactions
  // (which drops repayments) would hide every card payment and read too rosy.
  //
  // Planned series join as dated events rather than as transactions, so they
  // move the line without touching the variance the pessimistic band is built
  // from. One charged to a card is left out: it moves cash only when the card
  // is paid, and that payment is already sized from the card's balance.
  //
  // A recurring outflow that's due but hasn't posted is still owed, so it lands
  // tomorrow (missed.js). Computed on the cash lens like everything else here:
  // a card subscription that hasn't billed doesn't take cash tomorrow. Card
  // payments are left out — they're sized from the card's balance, not a cadence.
  const runway = useMemo(
    () => {
      const cashTxs = transactions.filter(isCashTx);
      const cardIds = new Set(accounts.filter((a) => a.type === 'credit').map((a) => a.id));
      const owed = missedCharges(cashTxs, seriesLifecycle, { excludeSeriesKeys: cardPayments.ownedSeriesKeys });
      return assessRunway(balance, cashTxs, {
        horizonDays: 60,
        floor: runwayFloor,
        extraEvents: [
          ...cardPayments.events,
          ...inFlightCashEvents(inFlight),
          ...plannedEvents(plannedSeries, { horizonDays: 60, excludeAccountIds: cardIds }),
          ...missedEvents(owed),
        ],
        excludeSeriesKeys: new Set([...(cardPayments.ownedSeriesKeys || []), ...endedKeys]),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [balance, transactions, creditAccountIds, runwayFloor, cardPayments, inFlight, plannedSeries, accounts, seriesLifecycle, endedKeys]
  );

  // Series to ask about on the Recurring tab — every lens, since a card
  // subscription that stops billing is just as worth asking about.
  const missedPrompts = useMemo(
    () => missedCharges(transactions, seriesLifecycle, { excludeSeriesKeys: cardPayments.ownedSeriesKeys }).filter((m) => m.prompt),
    [transactions, seriesLifecycle, cardPayments]
  );
  const missedMatchCandidates = useMemo(
    () => missedCandidates(transactions, missedPrompts, seriesLifecycle),
    [transactions, missedPrompts, seriesLifecycle]
  );

  // P10/P90 cone based on monthly noise in the last 6 completed months.
  // Bands collapse to the p50 line when we don't have enough history yet.
  const variance = varianceFromHistory(spendingTransactions, { lookbackMonths: 6 });
  const forecastBands = projectionBands(projection, variance);
  const forecastConfidenceLow = variance.sampleSize < 3 || !(variance.stddev > 0);

  // Per-scenario overlay projections. Each enabled scenario gets its own
  // doctored transaction list piped through buildProjection so the chart
  // can draw a dashed line per scenario.
  const SCENARIO_COLORS = ['#F39C42', '#9B6BE0', '#43BFA5', '#E0574F'];
  const forecastOverlays = scenarios
    .filter((s) => s.enabled)
    .map((s, i) => ({
      id: s.id,
      name: s.name,
      color: SCENARIO_COLORS[i % SCENARIO_COLORS.length],
      data: adaptProjection(
        buildProjection(balance, applyScenarios(spendingTransactions, [s]), 12, {
          goals,
          scaleDeltas: scaleDeltasOf([s]),
          planned: plansAfterScenarios(plannedSeries, [s]),
          excludeSeriesKeys: endedKeys,
        })
      ),
    }));
  const displayTx = pastTransactions.map(toDisplayTx);
  // Planned series join the detected ones so every Plan view (totals, the
  // scenario builder, Today's series lookup) sees them; each carries `planned`.
  const recurring = [
    // Ended series have no future, so the forward-looking views drop them.
    ...buildRecurringSeries(transactions, { seriesNames }).filter((r) => !endedKeys.has(r.key)),
    ...buildPlannedSeries(plannedSeries),
  ];
  const mRecurringNet = monthlyRecurringNet(recurring);
  // Only funded budgets, most-spent first.
  const makeBudgetRows = (spendingTx, priorSpendingTx) =>
    buildBudgetRows(envelopes, appCategories, spendingTx, {
      envelopeMeta,
      priorMonthTransactions: priorSpendingTx,
    })
      .filter((r) => r.hasLimit && r.limit > 0)
      .sort((a, b) => b.spent - a.spent);

  const budgetRows = makeBudgetRows(monthlySpendingTx, priorMonthSpendingTx);

  // Today's "Left to spend". Identical to budgetRows whenever the picker is on
  // this month; when it isn't, Today keeps reporting now rather than silently
  // showing another month's remaining budgets under a hero labelled "today".
  const todayBudgetRows = isViewingCurrentMonth
    ? budgetRows
    : makeBudgetRows(
        txInMonth(pastTransactions, currentMonthRef).filter(isSpendingTx),
        txInMonth(pastTransactions, priorMonthOf(currentMonthRef)).filter(isSpendingTx),
      );

  // Recurring auto-detection: ranked candidates + filter dismissed.
  // transferIds lets the detector waive its amount-spread filter for
  // card payments, whose amounts swing with the statement balance.
  const recurringCandidates = detectRecurringCandidates(transactions, {
    isDismissed: (key) => !!recurringDismissed[firebaseSafeKey(key)],
    transferIds,
  });

  // Possible first charges for planned series that couldn't link on their own.
  const planSuggestions = useMemo(
    () => matchPlans(transactions, plannedSeries).suggestions,
    [transactions, plannedSeries]
  );

  const handleUploadProfilePhoto = async (file) => {
    if (!user || !file) return;
    setUploadingProfilePhoto(true);
    try {
      const url = await uploadProfilePhoto(file, user.uid);
      setProfilePhotoUrl(url);
      showToast('success', 'Profile photo updated');
    } catch (err) {
      showToast('error', err?.message || 'Could not upload photo');
    } finally {
      setUploadingProfilePhoto(false);
    }
  };

  const handleRemoveProfilePhoto = async () => {
    if (!user || !profilePhotoUrl) return;
    setUploadingProfilePhoto(true);
    try {
      await removeProfilePhoto(user.uid);
      setProfilePhotoUrl(null);
      showToast('success', 'Profile photo removed');
    } catch (err) {
      showToast('error', err?.message || 'Could not remove photo');
    } finally {
      setUploadingProfilePhoto(false);
    }
  };

  // Same shape as the iOS splash: the mark, the name, a spinner, on the paper
  // the app is about to draw on.
  if (authPending) {
    return (
      <div className="auth-splash">
        <AppLogo size={64} />
        <h1>Prisma</h1>
        <div className="auth-splash-spinner spin" />
      </div>
    );
  }

  if (!user) return <Login onLoginSuccess={() => {}} authError={authError} />;

  if (!dataReady) {
    return (
      <div style={{ maxWidth: 600, margin: '60px auto', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="skeleton-bar" style={{ opacity: 1 - i * 0.1 }} />
        ))}
      </div>
    );
  }

  return (
    <>
      <FocusShell
        user={user}
        profilePhotoUrl={profilePhotoUrl}
        theme={theme}
        setTheme={setTheme}
        selectedMonth={selectedMonth}
        onSelectedMonthChange={setSelectedMonth}
        balance={balance}
        runway={runway}
        cards={cardSummaries}
        accounts={accounts}
        income={income}
        expenses={expenses}
        displayTx={displayTx}
        budgetRows={budgetRows}
        todayBudgetRows={todayBudgetRows}
        recurring={recurring}
        forecast={projection}
        categories={appCategories}
        categoryMeta={appCategoryMeta}
        onEditTransaction={(tx) => {
          const raw = tx.raw || tx;
          setEditTx(transactions.find((t) => t.id === raw.id) || raw);
        }}
        onDeleteTransaction={(tx) => {
          const id = tx.id || tx.raw?.id;
          if (id && window.confirm('Delete this transaction?')) delTx(id);
        }}
        onAddCategory={addCategory}
        onRemoveCategory={removeCategory}
        onRenameCategory={renameCategory}
        onSetCategoryAppearance={setCategoryAppearance}
        goals={goals}
        transactionsRaw={transactions}
        onSaveGoal={saveGoal}
        onDeleteGoal={deleteGoal}
        onArchiveGoal={archiveGoal}
        scenarios={scenarios}
        forecastOverlays={forecastOverlays}
        forecastBands={forecastBands}
        forecastConfidenceLow={forecastConfidenceLow}
        onSaveScenario={saveScenario}
        onDeleteScenario={deleteScenario}
        onToggleScenario={toggleScenarioEnabled}
        onSyncAll={handleSyncAll}
        bankSyncing={bankSyncing}
        hasBankSync={syncableEnrollments.length > 0}
        lastSyncedAt={lastSyncedAt}
        connectionIssues={enrollmentIssues}
        onReconnectBank={reconnectEnrollment}
        balanceAsOf={balanceAsOf}
        budgetProps={{
          transactions: monthlySpendingTx,
          priorMonthTransactions: priorMonthSpendingTx,
          allTransactions: transactions,
          envelopes,
          envelopeMeta,
          categories: appCategories,
          onSave: saveEnvelope,
          onDelete: deleteEnvelope,
          onToggleRollover: setEnvelopeRollover,
          onBulkSetLimits: bulkSetEnvelopeLimits,
        }}
        recurringProps={{
          transactions,
          seriesNames,
          categories: appCategories,
          onDeleteSeries: deleteSeriesInstances,
          onEditSeries: editSeriesInstances,
          suggestions: recurringCandidates,
          onConfirmSuggestion: confirmRecurringCandidate,
          onDismissSuggestion: dismissRecurringCandidate,
          planned: plannedSeries,
          accounts,
          onSavePlanned: savePlanned,
          onDeletePlanned: deletePlanned,
          planSuggestions,
          onLinkPlan: linkPlanToTransaction,
          onDismissPlanMatch: dismissPlanMatch,
          lifecycle: seriesLifecycle,
          missed: missedPrompts,
          missedCandidates: missedMatchCandidates,
          onLinkMissed: linkMissedCharge,
          onSkipMissed: skipMissedCharge,
          onEndSeries: endSeries,
          onResumeSeries: resumeSeries,
          onDismissMissedMatch: dismissMissedMatch,
        }}
        cashFlowProps={{
          balance,
          runway,
          forecast: projection,
          monthlyRecurringNet: mRecurringNet,
          recurring,
          forecastOverlays,
          forecastBands,
          forecastConfidenceLow,
        }}
        accountsProps={{
          accounts,
          cards: cardSummaries,
          pendingCardPayments,
          balance,
          bankConnections,
          bankSyncing,
          plaidReady: !plaidConnecting,
          onConnectBank: () => openPlaidLink(),
          onSyncBank: (enr) => syncBankConnection(enr, { live: true }),
          onReconnectBank: reconnectEnrollment,
          onRemoveBank: deleteBankConnection,
          onReCategorizeAccount: reCategorizeAccount,
          recategorizingAccountId,
          uncategorizedByAccount: (accountId) =>
            transactions.filter(t => t.accountId === accountId && t.category === 'Other' && !t.manualCategory).length,
          onAddManualAccount: addManualAccount,
          onRenameManualAccount: renameManualAccount,
          onEditManualBalance: editManualAccountBalance,
          onDeleteManualAccount: deleteManualAccount,
          onToggleAccountIncluded: setAccountIncludedInBalance,
        }}
        settingsProps={{
          runwayFloor,
          onRunwayFloorChange: saveRunwayFloor,
          budgetCount: budgetCountFromEnvelopes(envelopes, appCategories),
          categoryCount: appCategories.length,
          onInstall: async () => {
            if (!installPrompt) return;
            installPrompt.prompt();
            const { outcome } = await installPrompt.userChoice;
            if (outcome === 'accepted') setShowInstall(false);
            setIP(null);
          },
          showInstall: showInstall && !window.matchMedia('(display-mode:standalone)').matches,
          // Deliberate sign-out, so the offline grant goes with it — the next
          // person at this browser must pass a live check, not this one's.
          onSignOut: () => { clearGrant(); return signOut(auth); },
          onReCategorize: reCategorizeAll,
          recategorizingAI,
          uncategorizedCount: transactions.filter(t => t.category === 'Other' && !t.manualCategory).length,
          profilePhotoUrl,
          uploadingProfilePhoto,
          onUploadProfilePhoto: handleUploadProfilePhoto,
          onRemoveProfilePhoto: handleRemoveProfilePhoto,
        }}
      />


      <Modal open={!!editTx} onClose={() => setEditTx(null)} title="Edit Transaction">
        {editTx && (
          <EditTransaction
            tx={editTx}
            categories={appCategories}
            onSave={updateTx}
            onClose={() => setEditTx(null)}
          />
        )}
      </Modal>

    <Modal
      open={!!connectionHelp}
      onClose={() => setConnectionHelp(null)}
      title="Move This Connection to Plaid"
    >
      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 8 }}>
        {connectionHelp?.institution || 'This bank'} was linked through a provider this app no
        longer uses, so it can’t sync anymore.
      </p>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
        Link the bank again with Plaid (your imported transactions are kept), then remove the old
        connection.
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => { const id = connectionHelp?.id; setConnectionHelp(null); if (id) removeLegacyConnection(id); }}
          className="btn-danger is-block pressable"
        >
          Remove Old Connection
        </button>
        <button
          onClick={() => { setConnectionHelp(null); openPlaidLink(); }}
          className="btn-primary is-block pressable"
        >
          Link with Plaid
        </button>
      </div>
    </Modal>

    <Modal open={!!accountPickerData} onClose={() => setAccountPickerData(null)} title="Select Accounts">
      {accountPickerData && (
        <AccountPicker
          accounts={accountPickerData.accounts}
          onClose={() => setAccountPickerData(null)}
          onSelect={(selectedAccounts) => {
            const { connection } = accountPickerData;
            savePlaidConnection(connection.itemId, connection.institution, selectedAccounts)
              .then(saved => { if (saved) syncBankConnection(saved); })
              .catch(err => showToast('error', `Could not save the connection: ${err.message}`));
            setAccountPickerData(null);
          }}
        />
      )}
    </Modal>

      {plaidLinkToken && (
        <PlaidLink token={plaidLinkToken} onSuccess={handlePlaidSuccess} onExit={handlePlaidExit} />
      )}

      <Toast queue={toastQueue} onDismiss={dismissToast} />

      <SpeedInsights />
    </>
  );
}